import { useState, useEffect, useRef, useCallback } from "react";
import {
  ArrowLeft,
  MessageSquare,
  Send,
  Loader2,
  User,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  AlertCircle,
  FileText,
  BookOpen,
  BarChart3,
} from "lucide-react";
import {
  conversationalAI,
  type ConversationCitation,
  type ConversationTurnOut,
} from "../apiv1";
import GreenTendrils from "./GreenTendrils";
import KnowledgeBasePanel from "./KnowledgeBasePanel";

interface Props {
  onBack: () => void;
  projectId: string;
}

interface DisplayMessage {
  role: "user" | "assistant";
  content: string;
  turn?: ConversationTurnOut;
}

const EXAMPLE_QUESTIONS = [
  "How many records are there in total?",
  "Show me a breakdown by status",
  "What's changed most recently?",
];

// Phase 2 RAG design §6.1 — the three confidence tiers, each with its own
// UI treatment. Internal enum value is "guessing"; user-facing label is
// "Unverified" (§10 decision 1) — that mapping lives here, nowhere else.
function ConfidenceBadge({
  tier,
}: {
  tier: ConversationTurnOut["confidence_tier"];
}) {
  if (tier === "certain")
    return (
      <span className="ztd-badge ztd-badge-success">
        Certain — from connected data
      </span>
    );
  if (tier === "likely")
    return (
      <span className="ztd-badge ztd-badge-warning">
        Likely — inferred from available context
      </span>
    );
  if (tier === "guessing")
    // Deliberately NOT amber (§6.1: "a distinctly lower-emphasis
    // treatment") — a plain muted badge makes the absence of both
    // citations and confidence visually obvious, not just the label.
    return (
      <span className="ztd-badge border-white/10 bg-white/5 text-white/40">
        Unverified — no supporting evidence
      </span>
    );
  return null;
}

const CITATION_ICON: Record<ConversationCitation["type"], typeof FileText> = {
  query: BarChart3,
  kb_chunk: BookOpen,
  dq_finding: AlertCircle,
  glossary: FileText,
};

/** One assistant turn's provenance affordance — the resolved query-spec
 * IR/compiled SQL (Phase 1 §6.e), extended (Phase 2 §6.3) to render
 * `citations` generically per type rather than assuming SQL is the only
 * provenance shape a turn can have. */
function ProvenancePanel({ turn }: { turn: ConversationTurnOut }) {
  const [open, setOpen] = useState(false);
  const hasCitations = !!turn.citations && turn.citations.length > 0;
  if (!turn.compiled_sql && !turn.resolved_ir && !hasCitations) return null;
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[11px] text-white/30 hover:text-white/50 transition"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {turn.compiled_sql ? "View query" : "View sources"}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {turn.row_policy_applied && (
            <div className="flex items-center gap-1.5 text-[11px] text-brand-400">
              <ShieldCheck size={12} />
              Row-level access policy applied
            </div>
          )}
          {turn.compiled_sql && (
            <pre className="bg-surface-300 rounded-lg p-3 overflow-x-auto font-mono text-[11px] text-white/60">
              {turn.compiled_sql}
            </pre>
          )}
          {hasCitations && (
            <ul className="space-y-1">
              {turn.citations!.map((c, i) => {
                const Icon = CITATION_ICON[c.type] ?? FileText;
                return (
                  <li
                    key={i}
                    className="flex items-start gap-1.5 text-[11px] text-white/40 bg-surface-300 rounded-lg px-2.5 py-1.5"
                  >
                    <Icon size={12} className="text-ai flex-shrink-0 mt-0.5" />
                    <span className="min-w-0 truncate">{c.label}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function ConversationalAIView({ onBack, projectId }: Props) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState("");
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    conversationalAI
      .createSession(projectId)
      .then((session) => {
        if (!cancelled) setSessionId(session.id);
      })
      .catch((e: Error) => {
        if (!cancelled)
          setSessionError(e.message || "Failed to start a conversation.");
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, asking]);

  const ask = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || !sessionId || asking) return;
      setError(null);
      setInput("");
      setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
      setAsking(true);
      try {
        const turn = await conversationalAI.ask(sessionId, trimmed);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: turn.narrative ?? "", turn },
        ]);
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : "Something went wrong asking that question.",
        );
      } finally {
        setAsking(false);
      }
    },
    [sessionId, asking],
  );

  return (
    <div className="relative min-h-screen">
      <GreenTendrils />

      <div className="relative z-10 max-w-4xl mx-auto p-6 flex flex-col h-screen">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4 flex-shrink-0">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-surface-200 text-white/40 hover:text-white transition"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-ai/15">
            <MessageSquare size={20} className="text-ai" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Conversational AI</h1>
            <p className="text-xs text-white/30">
              Ask a question about this project's connected database
            </p>
          </div>
        </div>

        {sessionError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-2xl text-sm text-red-300 flex items-center gap-2">
            <AlertCircle size={14} /> {sessionError}
          </div>
        )}

        <div className="mb-4 flex-shrink-0">
          <KnowledgeBasePanel projectId={projectId} />
        </div>

        {/* Message list */}
        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 py-2">
          {!sessionError && messages.length === 0 && (
            <div className="text-center py-16 space-y-4">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-ai/10 flex items-center justify-center">
                <MessageSquare size={24} className="text-ai" />
              </div>
              <p className="text-sm text-white/40 max-w-sm mx-auto">
                Ask a business question in plain English — the agent resolves it
                against this project's connected schema and answers only from
                what it can actually query.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {EXAMPLE_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => ask(q)}
                    disabled={!sessionId || asking}
                    className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 border border-white/5 rounded-lg text-[11px] text-white/40 hover:text-white/60 transition-all disabled:opacity-40"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}
            >
              {msg.role === "assistant" && (
                <div className="w-7 h-7 rounded-lg bg-ai/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <MessageSquare size={14} className="text-ai" />
                </div>
              )}
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-ai text-white rounded-br-md"
                    : "bg-surface-200 text-gray-200 rounded-bl-md"
                }`}
              >
                {msg.role === "assistant" && msg.turn?.confidence_tier && (
                  <div className="mb-1.5">
                    <ConfidenceBadge tier={msg.turn.confidence_tier} />
                  </div>
                )}
                {msg.content}
                {msg.turn && <ProvenancePanel turn={msg.turn} />}
              </div>
              {msg.role === "user" && (
                <div className="w-7 h-7 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0 mt-1">
                  <User size={14} className="text-gray-300" />
                </div>
              )}
            </div>
          ))}

          {asking && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-lg bg-ai/20 flex items-center justify-center flex-shrink-0">
                <MessageSquare size={14} className="text-ai" />
              </div>
              <div className="bg-surface-200 rounded-2xl rounded-bl-md px-4 py-3">
                <Loader2 size={16} className="animate-spin text-gray-400" />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {error && (
          <div className="mb-2 p-2.5 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-300 flex items-center gap-2">
            <AlertCircle size={13} /> {error}
          </div>
        )}

        {/* Input */}
        <div className="flex-shrink-0 flex items-center gap-2 pt-2 pb-4">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                ask(input);
              }
            }}
            placeholder="Ask a question about this project's data..."
            disabled={!sessionId || asking}
            className="flex-1 bg-surface-200 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 transition disabled:opacity-50"
          />
          <button
            onClick={() => ask(input)}
            disabled={!sessionId || asking || !input.trim()}
            className="p-2.5 rounded-xl bg-ai hover:bg-ai/85 text-white transition disabled:opacity-40 disabled:hover:bg-ai"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
